//React
import React from "react"
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import toast from 'react-hot-toast';

// Styles
import "../../styles/dashboard.css";
import "../../styles/credit-card.css";

// App
import DashLayout from "../../layouts/dashboard/dashLayout";
import { Routes } from "../../routes";
import { Offcanvas, CreditHistoryModal, PlanChangeModal, PlanHistoryModal, PaymentMethodsModal, InvoicesModal, SubscriptionManagementModal, UnverifiedUsersModal } from "../modal";
import TopUpModal from "../modal/TopUpModal";
import { logout, useAppSelector } from "../../stores";
import { FamlaServices } from "../../services";
import { DashboardCard, Spiner, CreditCard, CreditActionsPanel } from "../../components";
import CreditsService from "../../services/api/creditsService";
import StripeService from "../../services/api/stripeService";
import PlansService from "../../services/api/plansService";

//Chart
import { ChartDashboard } from "../../chart";

//interface
import { iAlert } from "../../interfaces";
import { useTranslation } from "react-i18next";
import { getDashboardCardsData, useCountdown } from "../../utils";
import { Tutorial, FloatingTutorialButton } from "../../components";
import { useTutorial } from "../../hooks/useTutorial";
import { adminTutorialSteps } from "../../config/tutorialSteps";
import { useTheme } from "../../contexts/ThemeContext";
import { useBillingRecommendation } from "../../hooks/useBillingRecommendation";
type BarChartDataset = {
    label: any;
    data: number[];
    backgroundColor: string;
};

type BarChartData = {
    labels: string[];
    datasets: BarChartDataset[];
};

const DashboardViews = () => {
    const auth = useAppSelector(state => state.auth);
    const companyId = auth.session.company_id;
    const company = useAppSelector((state) => state.company.company);
    const { t } = useTranslation();
    const { theme } = useTheme();
    const isLightMode = theme === 'light';
    const { isMicrosoftTenant, isMicrosoftRecommended, marketplaceUrl, openMarketplace } = useBillingRecommendation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = React.useState(false)
    const [showCreditHistory, setShowCreditHistory] = React.useState(false)
    const [showTopUpModal, setShowTopUpModal] = React.useState(false)
    const [showPlanChange, setShowPlanChange] = React.useState(false)
    const [showPlanHistory, setShowPlanHistory] = React.useState(false)
    const [showPaymentMethods, setShowPaymentMethods] = React.useState(false)
    const [showInvoices, setShowInvoices] = React.useState(false)
    const [showSubscriptionManagement, setShowSubscriptionManagement] = React.useState(false)
    const [showUnverifiedUsers, setShowUnverifiedUsers] = React.useState(false)
    const [portalLoading, setPortalLoading] = React.useState(false)
    const [systemConfig, setSystemConfig] = React.useState<any>(null)
    const [state, setState] = React.useState({
        totalUsers: 0,
        totalRegisteredUsers: 0,
        unverifiedCount: 0,
        // AI Credits (UNIQUEMENT pour famla-app client)
        plan: 'FREE',
        accountStatus: 'ACTIVE',  // ACTIVE, EXHAUSTED, EXPIRED, GRACE_PERIOD
        creditsAvailable: 0,
        creditsQuota: 0,
        creditsConsumedMonth: 0,
        rolloverAvailable: 0,
        rolloverNextMonth: 0,
        monthlyCreditsQuota: 0,
        freeCreditsRemaining: 0,
        initialFreeCredits: 0,
        purchasedCreditsRemaining: 0,
        essentialPackBalance: 0,
        carriedPurchasedCredits: 0,
        giftCreditsRemaining: 0,
        isInGracePeriod: false,
    });

    // famla-app = AI Credits UNIQUEMENT (pas de fallback tokens)
    const cardsData = getDashboardCardsData(state, t, true);

    const [alert, setAlert] = React.useState<iAlert>({
        isActive: false,
        type: '',
        message: ''
    });
    
    let daysRemaing = useCountdown(company?.expired_at)

    // Tutorial setup
    const tutorialSteps = adminTutorialSteps(t);
    const {
        run: runTutorial,
        stepIndex,
        steps,
        startTutorial,
        handleJoyrideCallback,
        isTutorialCompleted,
        resetTutorialProgress
    } = useTutorial(tutorialSteps, 'admin', true); // Auto-start for new users

    const [barChartData, setBarChartData] = React.useState<BarChartData>({
        labels: [],
        datasets: [
            {
                label: "Credits AI",
                data: [],
                backgroundColor: "#03A9F4",
            },
        ],
    });

    // Détecter le retour d'achat Microsoft
    React.useEffect(() => {
        if (searchParams.get('purchase') === 'success' && searchParams.get('source') === 'microsoft') {
            const credits = searchParams.get('credits');
            toast.success(`🎉 ${t('microsoft.purchaseSuccessToast', { credits: credits ?? '' })}`, {
                duration: 5000,
                position: 'top-center'
            });
            
            // Nettoyer l'URL
            setSearchParams({});
        }
    }, [searchParams, setSearchParams, t]);

    React.useEffect(() => {
        // [AI CREDITS UNIQUEMENT] Dashboard client = Crédits AI seulement
        // Pour le dashboard backoffice, on affichera tokens + crédits
        async function loadAiCredits() {
            setLoading(true);

            try {
                const response = await CreditsService.getCreditBalance(companyId, auth.access_token);
                
                if (response.success && response.data) {
                    setState(prevState => ({
                        ...prevState,
                        plan: response.data.plan || 'FREE',
                        accountStatus: response.data.status || 'ACTIVE',
                        creditsAvailable: response.data.credits_available || 0,
                        creditsQuota: response.data.credits_quota || 0,
                        creditsConsumedMonth: response.data.credits_consumed_month || 0,
                        rolloverAvailable: response.data.rollover_available || 0,
                        rolloverNextMonth: response.data.rollover_next_month || 0,
                        monthlyCreditsQuota: response.data.monthly_credits_quota || 0,
                        freeCreditsRemaining: response.data.free_credits_remaining || 0,
                        initialFreeCredits: response.data.initial_free_credits || 0,
                        purchasedCreditsRemaining: response.data.purchased_credits_remaining || 0,
                        essentialPackBalance: response.data.essential_pack_balance || 0,
                        carriedPurchasedCredits: response.data.carried_purchased_credits || 0,
                        giftCreditsRemaining: response.data.gift_credits_remaining || 0,
                        isInGracePeriod: response.data.is_in_grace_period || false,
                    }));

                    
                    // [VÉRIFICATION EXPIRATION] Vérifier expiration basée sur crédits épuisés
                    if ((response.data.plan === 'ENTERPRISE' || response.data.plan === 'FREE') && response.data.credits_available <= 0) {
                        const planName = response.data.plan;
                        setAlert({
                            isActive: true,
                            type: 'warning',
                            message: planName === 'ENTERPRISE' 
                                ? (t('dashboard.enterpriseExpired') || 'Your ENTERPRISE plan has expired (credits exhausted). Please add credits to continue.')
                                : (t('dashboard.freeExpired') || 'Your FREE plan has expired (credits exhausted). Please add credits to continue.')
                        });
                    }
                } else if (response.error === "Unauthorized") {
                    setAlert({ isActive: true, type: "warning", message: 'You do not have authorisation to continue' });
                } else {
                    // Pas de fallback tokens pour famla-app : afficher valeurs par défaut
                    console.log('[Dashboard Client] AI Credits not found, showing default FREE plan');
                    setState(prevState => ({
                        ...prevState,
                        plan: 'FREE',
                        accountStatus: 'ACTIVE',
                        creditsAvailable: 0,
                        creditsQuota: 0,
                        creditsConsumedMonth: 0,
                        rolloverAvailable: 0,
                        rolloverNextMonth: 0,
                        monthlyCreditsQuota: 0,
                        freeCreditsRemaining: 0,
                        initialFreeCredits: 0,
                        purchasedCreditsRemaining: 0,
                        giftCreditsRemaining: 0,
                        isInGracePeriod: false,
                    }));
                }
            } catch (e) {
                console.error('[Dashboard Client] Load AI Credits error:', e);
                // Afficher valeurs par défaut si erreur API
                setState(prevState => ({
                    ...prevState,
                    plan: 'FREE',
                    accountStatus: 'ACTIVE',
                    creditsAvailable: 0,
                    creditsQuota: 0,
                    creditsConsumedMonth: 0,
                    rolloverAvailable: 0,
                    rolloverNextMonth: 0,
                    freeCreditsRemaining: 0,
                    initialFreeCredits: 0,
                    purchasedCreditsRemaining: 0,
                    giftCreditsRemaining: 0,
                    isInGracePeriod: false,
                }));
            } finally {
                setLoading(false);
            }
        }
        
        // Charger UNIQUEMENT les crédits AI (pas de tokens pour famla-app)
        loadAiCredits();
        
        // Charger la configuration système pour les prix top-up
        async function loadSystemConfig() {
            try {
                const response = await CreditsService.getSystemConfig(auth.access_token);
                if (response.success && response.data) {
                    setSystemConfig(response.data);
                }
            } catch (error) {
                console.error('[Dashboard] Failed to load system config:', error);
            }
        }
        loadSystemConfig();
        async function loadAllUsers() {
            setLoading(true);

            try {

                let data = await FamlaServices("api_host", `/users/company/${companyId}?page=${1}&limit=${1000000}`, "GET", "", auth.access_token);

                if (data.status === 200) {
                    const totalUser = data.body.data

                    setState(prevState => ({
                        ...prevState,
                        totalUsers: totalUser.total_count ?? (totalUser.count + totalUser.unverified_count),
                        totalRegisteredUsers: totalUser.count,
                        unverifiedCount: totalUser.unverified_count
                    }));
                    setLoading(false)
                }
                else if (data.status === 401 && data.body.description === "You do not have authorisation to continue") {
                    setAlert({ isActive: true, type: "warning", message: 'You do not have authorisation to continue' })

                }
                else if (data.status === 401) {
                    logout();
                }
                else {

                }
            }
            catch (e) {
                if (e instanceof Error) {
                    console.error(e.message);
                }
            } finally {
                setLoading(false);
            }
        }
        loadAllUsers();
        // [AI CREDITS] Graphique consommation mensuelle de crédits
        // ✅ Utilise endpoint backend GET /credits/history/monthly/{company_id}
        async function loadCreditsForTenMonths() {
            setLoading(true);

            try {
                // ✅ Appel API réel pour historique mensuel
                const response = await FamlaServices(
                    "api_host",
                    `/credits/history/monthly/${companyId}`,
                    "GET",
                    "",
                    auth.access_token
                );

                if (response.status === 200 && response.body?.data) {
                    const monthlyData = response.body.data;  // [{month: "Jan", total: 5, year: 2025}, ...]
                    
                    // Extraire labels et données
                    const labels = monthlyData.map((item: any) => item.month);
                    const datasetData = monthlyData.map((item: any) => item.total);

                    setBarChartData({
                        labels: labels,
                        datasets: [
                            {
                                label: "Crédits AI",
                                data: datasetData,
                                backgroundColor: "#03A9F4",
                            },
                        ],
                    });
                    
                    console.log(`[Dashboard] Loaded ${monthlyData.length} months of credit history`);
                } else if (response.status === 401) {
                    setAlert({ isActive: true, type: "warning", message: 'You do not have authorisation to continue' });
                } else {
                    // Fallback : afficher graphique vide si erreur
                    console.warn('[Dashboard] Credits history not available, showing empty chart');
                    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                    const currentMonth = new Date().getMonth();
                    const labels: string[] = [];
                    for (let i = 10; i >= 0; i--) {
                        labels.push(monthNames[(currentMonth - i + 12) % 12]);
                    }
                    setBarChartData({
                        labels: labels,
                        datasets: [{ label: "Crédits AI", data: new Array(11).fill(0), backgroundColor: "#03A9F4" }],
                    });
                }
            } catch (e) {
                console.error('[Dashboard] Load Credits History error:', e);
                // Fallback : graphique vide en cas d'erreur réseau
                const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                const currentMonth = new Date().getMonth();
                const labels: string[] = [];
                for (let i = 10; i >= 0; i--) {
                    labels.push(monthNames[(currentMonth - i + 12) % 12]);
                }
                setBarChartData({
                    labels: labels,
                    datasets: [{ label: "Crédits AI", data: new Array(11).fill(0), backgroundColor: "#03A9F4" }],
                });
            } finally {
                setLoading(false);
            }
        }

        loadCreditsForTenMonths();

    }, [auth.access_token, companyId]);

    // Fonction pour formater l'affichage du plan (Doc v2.2: jamais "FREE expired")
    const formatPlanDisplay = (plan: string, creditsAvailable: number) => {
        if (plan === 'FREE') {
            if (creditsAvailable <= 0) {
                return `${plan} - ${t('dashboard.consultationMode', 'Mode Consultation')}`;
            }
            return plan;
        }
        return plan;
    };

    // Fonction pour ouvrir le Customer Portal Stripe
    const handleCustomerPortal = async () => {
        try {
            setPortalLoading(true);
            console.log('[Dashboard] Opening Stripe Customer Portal for company:', companyId);
            
            const returnUrl = window.location.origin + '/dashboard';
            const response = await StripeService.createPortalSession(
                companyId,
                returnUrl,
                auth.access_token
            );

            console.log('[Dashboard] Portal session response:', JSON.stringify(response, null, 2));

            if (response.success && response.data) {
                // ✅ Vérifier que session_url existe bien
                const sessionUrl = response.data.session_url;
                console.log('[Dashboard] Portal session URL:', sessionUrl);
                
                if (sessionUrl) {
                    console.log('[Dashboard] Redirecting to Stripe Customer Portal...');
                    // Rediriger vers Stripe Customer Portal
                    window.location.href = sessionUrl;
                } else {
                    console.error('[Dashboard] session_url missing in response.data:', response.data);
                    setAlert({
                        isActive: true,
                        type: 'danger',
                        message: 'URL du portail client introuvable. Veuillez réessayer.'
                    });
                }
            } else {
                console.error('[Dashboard] Failed to create portal session:', response);
                console.error('[Dashboard] response.success:', response.success);
                console.error('[Dashboard] response.data:', response.data);
                const errorMessage = StripeService.formatErrorMessage(response);
                setAlert({
                    isActive: true,
                    type: 'danger',
                    message: `Erreur lors de l'accès au portail client: ${errorMessage}`
                });
            }
        } catch (error) {
            console.error('[Dashboard] Customer portal error:', error);
            setAlert({
                isActive: true,
                type: 'danger',
                message: 'Erreur lors de l\'ouverture du portail client. Veuillez réessayer.'
            });
        } finally {
            setPortalLoading(false);
        }
    };

    // Fonction pour ouvrir le modal Top-up
    const handleTopUp = () => {
        if (systemConfig) {
            setShowTopUpModal(true);
        } else {
            setAlert({
                isActive: true,
                type: 'warning',
                message: 'Configuration système non disponible. Veuillez réessayer.'
            });
        }
    };

    // Fonction pour gérer le paiement Top-up depuis le modal
    const handleTopUpPayment = async (topupCredits: number) => {
        try {
            if (isMicrosoftTenant) {
                if (marketplaceUrl) {
                    openMarketplace();
                } else {
                    setAlert({
                        isActive: true,
                        type: 'info',
                        message: t(
                            'dashboard.manageSubscriptionMicrosoftNoUrl',
                            'Votre abonnement est géré via Azure Marketplace. Contactez votre administrateur Azure ou le support Famla.'
                        ),
                    });
                }
                setShowTopUpModal(false);
                return;
            }

            setPortalLoading(true);
            console.log('[Dashboard] Initiating top-up payment for', topupCredits, 'credits, company:', companyId);
            
            const baseUrl = window.location.origin;
            const successUrl = StripeService.generateSuccessUrl(baseUrl, 'ESSENTIAL', true);
            const cancelUrl = StripeService.generateCancelUrl(baseUrl);
            
            // Créer session checkout pour top-up (plan=null car c'est un top-up, pas une activation)
            const response = await StripeService.createCheckoutSession(
                null, // ⚠️ Important: null pour top-up (pas de plan, uniquement topup_credits)
                companyId,
                topupCredits,
                successUrl,
                cancelUrl,
                auth.access_token
            );

            if (response.success && response.data) {
                console.log('[Dashboard] Top-up checkout session created, redirecting to:', response.data.session_url);
                // Rediriger vers Stripe Checkout
                window.location.href = response.data.session_url;
            } else {
                console.error('[Dashboard] Failed to create top-up checkout session:', response);
                const errorMessage = StripeService.formatErrorMessage(response);
                setAlert({
                    isActive: true,
                    type: 'danger',
                    message: `Erreur lors de la création du top-up: ${errorMessage}`
                });
            }
        } catch (error) {
            console.error('[Dashboard] Top-up payment error:', error);
            setAlert({
                isActive: true,
                type: 'danger',
                message: 'Erreur lors du top-up. Veuillez réessayer.'
            });
        } finally {
            setPortalLoading(false);
        }
    };

    const options = React.useMemo(() => {
        const axisColor = isLightMode ? '#4B5563' : '#E5E7EB';
        const gridColor = isLightMode ? 'rgba(17, 24, 39, 0.14)' : 'rgba(255, 255, 255, 0.14)';

        return {
            responsive: true,
            maintainAspectRatio: false,  // ✅ Permet au graphique de s'ajuster à la hauteur du conteneur
            scales: {
                x: {
                    grid: {
                        display: true,
                        color: gridColor,
                    },
                    ticks: {
                        color: axisColor,
                    },
                },
                y: {
                    beginAtZero: true,  // ✅ Commence l'axe Y à 0
                    grid: {
                        display: true,
                        color: gridColor,
                    },
                    ticks: {
                        color: axisColor,
                    },
                },
            },
            plugins: {
                legend: {
                    display: true,
                    labels: {
                        color: axisColor,
                    },
                },
            },
        };
    }, [isLightMode]);

    return (
        <DashLayout>
            {loading ?
                <Spiner className="text-warning spinner-border-sm" classParent="d-flex justify-content-center align-items-center mt-5" />
                :
                <div className="container-fluid p-4 tutorial-admin-welcome">

                    <div className="mb-3 d-flex justify-content-between align-items-center">
                        <div className="d-flex gap-2">
                            <h1 className="mb-0 fw-bold h4" style={{ color: 'var(--text-primary)' }}>
                                <NavLink to={Routes.HOMEVIEW} className="fa-solid fa-arrow-left text-decoration-none m-none" style={{ color: 'var(--text-primary)' }}></NavLink> {t('dashboard.dashboard')}
                            </h1>
                            <h1 className="web-none" data-bs-toggle='offcanvas' data-bs-target='#offcanvas'>
                                <i className="fa-solid fa-bars fs-5" style={{ color: 'var(--text-primary)' }}></i>
                            </h1>
                        </div>
                        <div>
                            <NavLink to={Routes.HOMEVIEW} className={'text-danger text-end'}>{t("dashboard.exitDashboard")}</NavLink>
                        </div>

                    </div>
                    <div className="row" data-tutorial="admin-overview">

                        {cardsData.map((card, index) => (
                            <DashboardCard
                                key={index}
                                title={card.title}
                                iconClass={card.iconClass}
                                value={card.value}
                                bgColor={card.bgColor}
                                onClick={card.title === t('dashboard.unverifiedUsers') ? () => setShowUnverifiedUsers(true) : undefined}
                            />
                        ))}
                    </div>
                    <div className="row">
                        <div className="col-xl-8 col-lg-12 col-md-12 col-12" data-tutorial="admin-charts">
                            <div className="card mb-4 border-0" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)' }}>
                                <div className="card-header align-items-center card-header-height d-flex" style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderBottom: '1px solid var(--border-subtle)' }}>
                                    <div>
                                        <h4 className="mb-0">
                                            {t('dashboard.monthlyCreditConsumption')}
                                        </h4>
                                    </div>
                                </div>
                                <div className="card-body" style={{ background: 'var(--bg-secondary)' }}>
                                    <div id="earning" className="apex-charts" style={{ height: '400px' }}>
                                        <ChartDashboard type="bar" data={barChartData} options={options} />
                                    </div>

                                </div>
                            </div>
                        </div>
                        <div className="col-xl-4 col-lg-12 col-md-12 col-12">
                            {/* Carte de crédits - Style CreditsPanel avec toute la logique */}
                            <div data-tutorial="admin-credits">
                                <CreditCard
                                plan={state.plan}
                                creditsAvailable={state.creditsAvailable}
                                creditsQuota={state.creditsQuota}
                                creditsConsumedMonth={state.creditsConsumedMonth}
                                rolloverAvailable={state.rolloverAvailable}
                                rolloverNextMonth={state.rolloverNextMonth}
                                monthlyCreditsQuota={state.monthlyCreditsQuota}
                                freeCreditsRemaining={state.freeCreditsRemaining}
                                initialFreeCredits={state.initialFreeCredits}
                                purchasedCreditsRemaining={state.purchasedCreditsRemaining}
                                essentialPackBalance={state.essentialPackBalance}
                                carriedPurchasedCredits={state.carriedPurchasedCredits}
                                giftCreditsRemaining={state.giftCreditsRemaining}
                                accountStatus={state.accountStatus}
                                isInGracePeriod={state.isInGracePeriod}
                                />
                            </div>

                            {/* Panneau des actions - Toutes les fonctionnalités intégrées */}
                            <div data-tutorial="admin-actions">
                                <CreditActionsPanel
                                plan={state.plan}
                                creditsAvailable={state.creditsAvailable}
                                companyHasStripeCustomer={!!company?.stripe_customer_id}
                                useMicrosoftMarketplace={isMicrosoftTenant}
                                onManageSubscription={() => {
                                    if (isMicrosoftTenant) {
                                        if (marketplaceUrl) {
                                            openMarketplace();
                                        } else {
                                            setAlert({
                                                isActive: true,
                                                type: 'info',
                                                message: t(
                                                    'dashboard.manageSubscriptionMicrosoftNoUrl',
                                                    'Votre abonnement est géré via Azure Marketplace. Contactez votre administrateur Azure ou le support Famla.'
                                                ),
                                            });
                                        }
                                        return;
                                    }
                                    setShowSubscriptionManagement(true);
                                }}
                                onTopUp={handleTopUp}
                                onViewHistory={() => setShowCreditHistory(true)}
                                // onChangePlan={() => setShowPlanChange(true)}
                                onPlanHistory={() => setShowPlanHistory(true)}
                                onViewInvoices={() => setShowInvoices(true)}
                                onViewPaymentMethods={() => setShowPaymentMethods(true)}
                                // onCustomerPortal={handleCustomerPortal}
                                loading={portalLoading}
                                />
                            </div>
                        </div>

                    </div>

                    <Offcanvas />
                    
                    {/* Modal Historique Crédits */}
                    {showCreditHistory && (
                        <CreditHistoryModal
                            id="creditHistoryModal"
                            companyId={companyId}
                            accessToken={auth.access_token}
                            onClose={() => setShowCreditHistory(false)}
                        />
                    )}

                    {/* Modal Top-up Crédits */}
                    {showTopUpModal && systemConfig && (
                        <TopUpModal
                            id="topUpModal"
                            config={systemConfig}
                            currentCredits={state.creditsAvailable}
                            onClose={() => setShowTopUpModal(false)}
                            onTopUpRequest={handleTopUpPayment}
                            useMicrosoftMarketplace={isMicrosoftTenant}
                            marketplaceUrl={marketplaceUrl}
                        />
                    )}

                    {/* Modal Changement de plan */}
                    {showPlanChange && (
                        <PlanChangeModal
                            id="planChangeModal"
                            currentPlan={state.plan}
                            companyId={companyId}
                            accessToken={auth.access_token}
                            onClose={() => setShowPlanChange(false)}
                            onSuccess={() => {
                                setShowPlanChange(false);
                                // Recharger les crédits après changement
                                window.location.reload();
                            }}
                        />
                    )}

                    {/* Modal Historique des plans */}
                    {showPlanHistory && (
                        <PlanHistoryModal
                            id="planHistoryModal"
                            companyId={companyId}
                            accessToken={auth.access_token}
                            onClose={() => setShowPlanHistory(false)}
                        />
                    )}

                    {/* Modal Méthodes de paiement */}
                    {showPaymentMethods && (
                        <PaymentMethodsModal
                            id="paymentMethodsModal"
                            companyId={companyId}
                            accessToken={auth.access_token}
                            onClose={() => setShowPaymentMethods(false)}
                        />
                    )}

                    {/* Modal Factures */}
                    {showInvoices && (
                        <InvoicesModal
                            id="invoicesModal"
                            companyId={companyId}
                            accessToken={auth.access_token}
                            onClose={() => setShowInvoices(false)}
                        />
                    )}

                    {/* Modal Gestion Subscription */}
                    {showSubscriptionManagement && (
                        <div data-tutorial="admin-subscription">
                            <SubscriptionManagementModal
                            id="subscriptionManagementModal"
                            companyId={companyId}
                            accessToken={auth.access_token}
                            onClose={() => setShowSubscriptionManagement(false)}
                            onSuccess={() => {
                                setShowSubscriptionManagement(false);
                                // Recharger pour refléter les changements
                                window.location.reload();
                            }}
                            />
                        </div>
                    )}

                    {/* Modal Utilisateurs Non Vérifiés */}
                    {showUnverifiedUsers && (
                        <UnverifiedUsersModal
                            id="unverifiedUsersModal"
                            companyId={companyId}
                            accessToken={auth.access_token}
                            onClose={() => setShowUnverifiedUsers(false)}
                        />
                    )}

                    {/* Tutorial Component */}
                    <Tutorial
                        run={runTutorial}
                        steps={steps}
                        stepIndex={stepIndex}
                        callback={handleJoyrideCallback}
                        continuous={true}
                        showProgress={true}
                        showSkipButton={true}
                    />

                    {/* Floating Tutorial Button */}
                    <FloatingTutorialButton onClick={startTutorial} />
                </div>
            }
        </DashLayout>
    )
}

export default DashboardViews;